import api from './api'

export type VMInstanceDetails = {
  id: string
  name: string
  description?: string
  status: string
  billing_status?: string
  created_at: string
  updated_at: string
  product?: {
    id: string
    name: string
    product_type: string
    pricing_model?: string
    price_per_cpu?: number
    price_per_memory_gb?: number
    price_per_storage_gb?: number
    base_price?: number
  }
  unit_price?: number
  markup_percentage?: number
  cluster?: {
    id: string
    name: string
  }
  proxmox_details?: {
    node: string
    vmid: number
    status: string
    uptime?: number
    cpu_cores?: number
    memory_gb?: number
    /** Soma de TODOS os discos da VM — inclui os block storages anexados. */
    disk_gb?: number
    /**
     * Só o disco de boot (issue #759) — é o que o resize da VM altera e o piso do
     * aumento. `null`/ausente quando o config não permite afirmar qual é: aí a tela
     * não oferece o aumento, em vez de cair no total.
     */
    system_disk_gb?: number | null
    system_disk_slot?: string | null
    system_disk_storage?: string | null
    /**
     * Por que `system_disk_gb` veio nulo. `boot_disk_not_found` é problema do
     * config da VM; `volume_without_address` é vínculo residual de um volume no
     * banco — diagnósticos diferentes, ações diferentes.
     */
    system_disk_issue?: 'boot_disk_not_found' | 'volume_without_address' | 'lookup_failed' | null
    os_type?: string
    ip_addresses?: string[]
    cpu_usage?: number
    memory_usage_percent?: number
    disk_read_bytes?: number
    disk_write_bytes?: number
    network_in_bytes?: number
    network_out_bytes?: number
    guest_os_info?: {
      name?: string
      'pretty-name'?: string
      version?: string
      'version-id'?: string
      'kernel-release'?: string
      'kernel-version'?: string
      machine?: string
      id?: string
    }
    guest_hostname?: string | null
  }
  public_ip?: VMPublicIPInfo | null
  /**
   * Modo de rede atual (issue #761). Decide qual troca o portal oferece: dentro da
   * VPC, "expor na internet"; fora dela, "voltar para a VPC". Opcional porque
   * backend anterior à #761 não devolve o campo — nesse caso trate como 'vpc'.
   */
  network_mode?: 'vpc' | 'public_bridge'
  /**
   * Em `public_bridge`, se o endereço é lease de DHCP da rede WAN do provedor (issue
   * #1061). Aí `public_ip` é nulo POR CONSTRUÇÃO — não há IP do pool reservado nem
   * cobrado — e o endereço que a tela mostra vem do guest agent e pode mudar a cada
   * renovação. Ausente em backend anterior à #1061: trate como `false`.
   */
  wan_dhcp?: boolean
  os_display_name?: string | null
  /**
   * VM que existe no servidor com a criação pela metade (issue #803): o clone
   * concluiu (ou pode ter concluído) e a cauda não rodou — credencial de
   * cloud-init, IP, `nameserver` e o resize para o disco contratado vêm todos
   * DEPOIS dele.
   *
   * Não olhe o `status` para isso: a reconciliação o concilia com o cluster e a
   * VM aparece `running`, igual a qualquer outra. Ausente em backend anterior à
   * #803 — leia sempre por `provisionamentoIncompleto`, que trata isso como
   * "não", e nunca por `!== false`.
   */
  provisioning_incomplete?: boolean
  provisioning_incomplete_at?: string | null
  /**
   * Família do SO derivada do TEMPLATE, no backend (issue #729).
   *
   * Preferir SEMPRE este campo ao `proxmox_details.os_type`: aquele é dado vivo do PVE
   * e some quando a consulta falha (`proxmox_details` vira `{ error }`), fazendo
   * qualquer teste de "é Windows?" virar `false` justamente quando o cluster está
   * degradado.
   *
   * `null` = indeterminado (VM sem template e sem pista do guest agent) — NÃO significa
   * Linux.
   *
   * Tipado como `string` de propósito, e não como união fechada: hoje o backend só
   * devolve `'windows'`, `'linux'` ou `null` (o desconhecido nunca sai como texto cru),
   * mas nada impede que ele ganhe uma família nova — e a união fechada faria um `switch`
   * exaustivo compilar e errar em produção no dia em que isso acontecesse. Compare
   * sempre com `'windows'` explicitamente, nunca por exclusão.
   */
  os_family?: string | null
  /**
   * Se `initial_user`/`initial_password` descrevem o login real da VM. Falso em Windows
   * enquanto a senha não tiver sido aplicada dentro do guest — o par gerado na criação
   * depende do Cloudbase-Init e o usuário gravado nem existe na máquina.
   */
  supports_cloudinit_credentials?: boolean
  initial_user?: string | null
  initial_password?: string | null
}

export type VMSnapshot = {
  name: string
  description?: string
  snaptime?: number
  vmstate?: boolean
  parent?: string
  // Enriquecimento via snapshot_usage_records (último registro coletado).
  // Ausente quando o coletor ainda não rodou ou o backend não é suportado.
  size_bytes?: number
  size_gb?: number
  daily_cost?: number
  estimated_monthly_cost?: number
  currency?: string
  storage_backend?: string
  last_collected_at?: string
  usage_notes?: string | null
  age_days?: number | null
}

export type UpdateInstanceRequest = {
  name?: string
  description?: string
  cores?: number
  memory?: number
  disk_gb?: number
}

export type RRDDataPoint = {
  time: number
  cpu?: number
  maxcpu?: number
  mem?: number
  maxmem?: number
  netin?: number
  netout?: number
  diskread?: number
  diskwrite?: number
}

export type RRDDataResponse = {
  data: RRDDataPoint[]
  timeframe: string
  cf: string
}

export type CreateSnapshotRequest = {
  snapname: string
  description?: string
  vmstate?: boolean
}

export type VMNICInfo = {
  net_id: string
  model: string
  mac_address: string | null
  bridge: string | null
  firewall: boolean
  vlan_tag: number | null
  is_primary: boolean
  vpc: { id: string; name: string } | null
  /**
   * NIC ligada ao pool de IPs públicos do Cloud Provider (issue #733). O pool público
   * também é um NetworkPool, então sem este marcador ela se disfarçava de VPC do
   * cliente — e exibia o nome interno do pool do CP. Quando `true`, `vpc` vem nulo.
   */
  is_public: boolean
  ip_addresses: string[]
}

/**
 * IPv4 público reservado para a VM. Vem do BANCO, não do guest agent: continua
 * visível com a VM parada, porque o IP segue reservado e cobrado.
 */
export type VMPublicIPInfo = {
  id: string
  ip_address: string
  gateway: string | null
  netmask: string | null
  status: string
  unit_price: number | null
  billing_cycle: string | null
}

export type VMNICsResponse = {
  nics: VMNICInfo[]
  total: number
  max_nics: number
  public_ip: VMPublicIPInfo | null
}

export type AddNICRequest = {
  network_id: string
  firewall?: boolean
}

export type UpdateNICRequest = {
  network_id?: string
  firewall?: boolean
}

export type RebuildTemplate = {
  id: string
  name: string
  category: string
  os_type: string | null
  os_variant: string | null
  is_current: boolean
}

export type RebuildTemplatesResponse = {
  templates: RebuildTemplate[]
  current_template_id: string | null
  current_template_name: string | null
}

/**
 * Uma linha do histórico da instância (issue #794).
 *
 * `action` é o que a tela rotula — `message` vem cru do backend, em inglês e em jargão
 * ("VM ... created successfully with VMID 130"), e só serve de detalhe/fallback.
 * `user_email`/`user_name` vêm `null` em evento gravado por task (sem autor humano).
 */
export type VMHistoryEntry = {
  id: string
  action: string
  level: string
  /**
   * `null` quando o backend corta a mensagem: eventos de warning/erro cujo texto não foi
   * escrito por nós interpolam a exceção do Proxmox (nome de node, storage, caminho) e
   * não podem sair para um portal white-label. Quem rotula a linha é sempre a `action`.
   */
  message: string | null
  details: Record<string, any>
  user_id: string | null
  user_email: string | null
  user_name: string | null
  created_at: string
}

export type VMHistoryResponse = {
  history: VMHistoryEntry[]
  /** Total de eventos da VM — NÃO o tamanho desta página. */
  total: number
  limit: number
  /**
   * Cursor da página seguinte (`<created_at ISO>|<id>`), ou `null` no fim da lista.
   *
   * A paginação é por cursor e não por offset porque o histórico cresce pela ponta em que
   * se lê: um evento gravado entre dois cliques em "Carregar mais" empurraria a janela do
   * offset e a página seguinte repetiria linhas já vistas.
   */
  next_cursor: string | null
}

/** Janela do RRD do PVE -> janela do endpoint de métricas (#1187). `year` (>90d) não existe lá. */
const JANELA_DE_METRICAS: Record<string, string> = {
  hour: '1h',
  day: '24h',
  week: '7d',
  month: '30d',
}

type PontoDeMetricas = {
  t: number
  cpu: number | null
  mem_used: number | null
  mem_max: number | null
  disk_read_bps: number | null
  disk_write_bps: number | null
  net_in_bps: number | null
  net_out_bps: number | null
}

/**
 * Converte a série do endpoint de métricas no formato do RRD que a tela já consome.
 * CPU vem em % (0–100) e o RRD em fração, com `maxcpu` 1. Taxa `null` (primeiro ponto ou
 * reinício da VM) segue `undefined`: a tela não recebe um zero inventado do cliente.
 */
export function metricasParaRRD(pontos: PontoDeMetricas[]): RRDDataPoint[] {
  return pontos.map((p) => ({
    time: p.t,
    cpu: p.cpu == null ? undefined : p.cpu / 100,
    maxcpu: 1,
    mem: p.mem_used ?? undefined,
    maxmem: p.mem_max ?? undefined,
    netin: p.net_in_bps ?? undefined,
    netout: p.net_out_bps ?? undefined,
    diskread: p.disk_read_bps ?? undefined,
    diskwrite: p.disk_write_bps ?? undefined,
  }))
}

const instancesApi = {
  /**
   * Get detailed information about a specific VM instance
   */
  getInstanceDetails: async (instanceId: string): Promise<VMInstanceDetails> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}`)
    return response.data
  },

  /**
   * Get RRD metrics time-series for a VM (CPU, memory, network, disk I/O)
   */
  getRRDData: async (
    instanceId: string,
    timeframe: string = 'hour',
    cf: string = 'AVERAGE',
  ): Promise<RRDDataResponse> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}/rrddata`, {
      params: { timeframe, cf },
    })
    return response.data
  },

  /**
   * Série da VM pela plataforma (#1187), caindo no RRD do PVE quando não há série (ingestão
   * desligada, VM sem pontos, provedor fora, janela de um ano ou qualquer erro).
   */
  getMetricasDaVM: async (
    instanceId: string,
    timeframe: string = 'hour',
    cf: string = 'AVERAGE',
  ): Promise<RRDDataResponse> => {
    const janela = JANELA_DE_METRICAS[timeframe]
    if (janela) {
      try {
        const r = await api.get(`/api/v1/metrics/vms/${instanceId}`, { params: { janela } })
        if (r.data?.tem_serie && Array.isArray(r.data.pontos)) {
          return { data: metricasParaRRD(r.data.pontos), timeframe, cf }
        }
      } catch {
        // cai no RRD abaixo
      }
    }
    return instancesApi.getRRDData(instanceId, timeframe, cf)
  },

  /**
   * Update instance configuration
   */
  updateInstance: async (instanceId: string, data: UpdateInstanceRequest): Promise<any> => {
    const params = new URLSearchParams()
    if (data.name) params.append('name', data.name)
    if (data.description) params.append('description', data.description)
    if (data.cores) params.append('cores', data.cores.toString())
    if (data.memory) params.append('memory', data.memory.toString())
    if (data.disk_gb) params.append('disk_gb', data.disk_gb.toString())

    const response = await api.put(
      `/api/v1/end-customer/instances/${instanceId}?${params.toString()}`,
      {},
    )
    return response.data
  },

  /**
   * List all snapshots for a VM
   */
  listSnapshots: async (instanceId: string): Promise<VMSnapshot[]> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}/snapshots`)
    return response.data.snapshots
  },

  /**
   * Create a snapshot
   */
  createSnapshot: async (instanceId: string, data: CreateSnapshotRequest): Promise<any> => {
    const params = new URLSearchParams()
    params.append('snapname', data.snapname)
    if (data.description) params.append('description', data.description)
    if (data.vmstate !== undefined) params.append('vmstate', data.vmstate.toString())

    const response = await api.post(
      `/api/v1/end-customer/instances/${instanceId}/snapshots?${params.toString()}`,
      {},
    )
    return response.data
  },

  /**
   * Delete a snapshot
   */
  deleteSnapshot: async (instanceId: string, snapname: string): Promise<any> => {
    const response = await api.delete(
      `/api/v1/end-customer/instances/${instanceId}/snapshots/${snapname}`,
    )
    return response.data
  },

  /**
   * Rollback to a snapshot
   */
  rollbackSnapshot: async (instanceId: string, snapname: string): Promise<any> => {
    const response = await api.post(
      `/api/v1/end-customer/instances/${instanceId}/snapshots/${snapname}/rollback`,
      {},
    )
    return response.data
  },

  /**
   * Refresh snapshots usage (size_gb, estimated_monthly_cost) on demand.
   * Calls the nebula-agent via gRPC and updates snapshot_usage_records.
   */
  refreshSnapshotsUsage: async (
    instanceId: string,
  ): Promise<{ success: boolean; snapshots_recorded: number }> => {
    const response = await api.post(
      `/api/v1/end-customer/instances/${instanceId}/snapshots/refresh-usage`,
      {},
    )
    return response.data
  },

  /**
   * Change VM network connection to a different VPC.
   *
   * A VM NÃO é parada nem reiniciada: o backend preserva a config da NIC e troca
   * só o `bridge=` (por isso a resposta sempre traz `was_restarted: false`).
   *
   * `confirmNoGateway` libera o destino sem Cloud Gateway — sem ele o backend
   * recusa com 400 e `error.code === 'vpc_without_gateway'` (issue #727). Quando
   * a troca acontece nesse estado, a resposta traz `warnings`.
   */
  changeNetwork: async (
    instanceId: string,
    networkId: string,
    options?: { confirmNoGateway?: boolean },
  ): Promise<{
    success: boolean
    message: string
    instance: { id: string; name: string }
    network: { id: string; name: string; identifier: string; type: string }
    was_restarted: boolean
    /** Opcional de propósito: backend anterior à #727 não devolve o campo. */
    warnings?: string[]
  }> => {
    const response = await api.post(`/api/v1/end-customer/instances/${instanceId}/network`, {
      network_id: networkId,
      confirm_no_gateway: options?.confirmNoGateway ?? false,
    })
    return response.data
  },

  /**
   * Troca o MODO de rede da VM: tira-a da VPC e a expõe na internet com um IP
   * público fixo, ou a traz de volta para uma VPC (issue #761).
   *
   * Não confundir com `changeNetwork`, que move a VM ENTRE VPCs. Aqui muda como a
   * VM ganha endereço: dentro da VPC é DHCP do Cloud Gateway, fora é IP estático
   * aplicado por cloud-init — por isso a VM **precisa ser reiniciada** para o
   * endereço novo valer (`requires_restart`), e nenhuma das duas pontas reinicia
   * por conta própria.
   *
   * Recusas com `code` estruturado (leia com `getApiErrorCode`):
   * - `public_exposure_unconfirmed`: falta `confirmExposure` — a VM sai de trás do
   *   NAT do gateway e passa a ser alcançável pela internet;
   * - `vm_sem_cloud_init`: sem o drive não há como aplicar o IP dentro da VM, e a
   *   operação é recusada INTEIRA (a bridge não é trocada);
   * - `vpc_without_gateway`: falta `confirmNoGateway` na volta (issue #727).
   */
  changeNetworkMode: async (
    instanceId: string,
    params: {
      mode: 'vpc' | 'public_bridge'
      publicIpAddressId?: string
      networkPoolId?: string
      confirmExposure?: boolean
      confirmNoGateway?: boolean
    },
  ): Promise<{
    success: boolean
    message: string
    instance: { id: string; name: string }
    network_mode: 'vpc' | 'public_bridge'
    public_ip: VMPublicIPInfo | null
    network: { id: string; name?: string; identifier: string; type: string } | null
    requires_restart: boolean
    warnings: string[]
  }> => {
    const response = await api.post(`/api/v1/end-customer/instances/${instanceId}/network-mode`, {
      mode: params.mode,
      public_ip_address_id: params.publicIpAddressId ?? null,
      network_pool_id: params.networkPoolId ?? null,
      confirm_exposure: params.confirmExposure ?? false,
      confirm_no_gateway: params.confirmNoGateway ?? false,
    })
    return response.data
  },

  /**
   * Perform an action on a VM instance (start, stop, restart)
   */
  performAction: async (
    instanceId: string,
    action: 'start' | 'stop' | 'restart',
  ): Promise<{
    success: boolean
    message: string
    instance: {
      id: string
      name: string
      previous_status: string
      new_status: string
    }
  }> => {
    const response = await api.post(`/api/v1/end-customer/instances/${instanceId}/action`, {
      action,
    })
    return response.data
  },

  /**
   * Reset cloud-init credentials (password) for a VM instance
   */
  resetCredentials: async (
    instanceId: string,
    // `username` existe pelo Windows (#729): o usuário registrado na criação vem do fluxo
    // de cloud-init Linux e não existe na máquina; a conta real pode ter sido renomeada.
    data: { new_password: string; username?: string },
  ): Promise<{
    success: boolean
    message: string
  }> => {
    const response = await api.post(
      `/api/v1/end-customer/instances/${instanceId}/reset-credentials`,
      data,
    )
    return response.data
  },

  /**
   * List all NICs attached to a VM instance
   */
  listNICs: async (instanceId: string): Promise<VMNICsResponse> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}/nics`)
    return response.data
  },

  /**
   * Add a new NIC to a VM instance
   */
  addNIC: async (
    instanceId: string,
    data: AddNICRequest,
  ): Promise<{
    success: boolean
    message: string
    nic: { net_id: string; bridge: string; vpc: { id: string; name: string }; firewall: boolean }
    was_restarted: boolean
  }> => {
    const response = await api.post(`/api/v1/end-customer/instances/${instanceId}/nics`, data)
    return response.data
  },

  /**
   * Remove a NIC from a VM instance
   */
  removeNIC: async (
    instanceId: string,
    netId: string,
  ): Promise<{
    success: boolean
    message: string
    was_restarted: boolean
  }> => {
    const response = await api.delete(`/api/v1/end-customer/instances/${instanceId}/nics/${netId}`)
    return response.data
  },

  /**
   * Update a NIC on a VM instance (change VPC, toggle firewall)
   */
  updateNIC: async (
    instanceId: string,
    netId: string,
    data: UpdateNICRequest,
  ): Promise<{
    success: boolean
    message: string
    nic: {
      net_id: string
      bridge: string
      firewall: boolean
      vpc: { id: string; name: string } | null
      mac_address: string | null
    }
    was_restarted: boolean
  }> => {
    const response = await api.put(
      `/api/v1/end-customer/instances/${instanceId}/nics/${netId}`,
      data,
    )
    return response.data
  },
  /**
   * Get templates available for rebuild (same category as current template)
   */
  getRebuildTemplates: async (instanceId: string): Promise<RebuildTemplatesResponse> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}/rebuild-templates`)
    return response.data
  },

  /**
   * Histórico de atividades da instância (issue #794).
   *
   * Endpoint dedicado, e não `/logs/organization` com filtro de recurso: aquele responde
   * pela organização inteira e devolveria a atividade das outras VMs do cliente dentro do
   * histórico de uma.
   */
  getInstanceHistory: async (
    instanceId: string,
    params: { before?: string | null; limit?: number } = {},
  ): Promise<VMHistoryResponse> => {
    const response = await api.get(`/api/v1/end-customer/instances/${instanceId}/history`, {
      params: {
        limit: params.limit ?? 50,
        ...(params.before ? { before: params.before } : {}),
      },
    })
    return response.data
  },

  /**
   * Rebuild (reinstall) a VM instance with a different template
   */
  rebuildInstance: async (
    instanceId: string,
    templateId: string,
  ): Promise<{
    success: boolean
    message: string
    instance: { id: string; name: string; status: string }
  }> => {
    const response = await api.post(`/api/v1/end-customer/instances/${instanceId}/rebuild`, {
      template_id: templateId,
    })
    return response.data
  },
}

export default instancesApi
